import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { isRovingKey, resolveRovingIndex } from './rovingFocus';
import { usePopoverClamp } from './usePopoverClamp';

export interface MenuOption<T extends string> {
  value: T;
  label: string;
  /** Icône optionnelle (SVG Heroicon) affichée avant le libellé. */
  icon?: React.ReactNode;
}

interface MenuProps<T extends string> {
  /** Contenu du bouton déclencheur (icône). */
  trigger: React.ReactNode;
  /** Libellé accessible du déclencheur (aria-label + title). */
  label: string;
  options: readonly MenuOption<T>[];
  value: T;
  onSelect: (value: T) => void;
  /** Libellé de fermeture (a11y), fourni par i18n. */
  closeLabel: string;
  /**
   * Classes du bouton déclencheur. Par défaut : bouton-icône carré (`h-11 w-11`).
   * Fournir une classe pour un déclencheur ÉTIQUETÉ (ex. drapeau + nom + chevron).
   */
  triggerClassName?: string;
}

const DEFAULT_TRIGGER_CLASS =
  'flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none';

/**
 * Menu déroulant accessible et générique (sélection unique).
 *
 * Clavier : Entrée/Espace/Flèches ouvrent, `Échap` ferme et rend le focus au
 * déclencheur, `↑`/`↓` naviguent, `Home`/`End` bornent, Entrée/Espace valident.
 * Clic extérieur ferme. `role="menu"` + `aria-checked` sur l'option active.
 */
export function Menu<T extends string>({
  trigger,
  label,
  options,
  value,
  onSelect,
  closeLabel,
  triggerClassName,
}: MenuProps<T>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      0,
      options.findIndex((o) => o.value === value),
    ),
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  /** Maintien dans le cadre visible — noyau commun à tous les popovers ancrés. */
  const panelRef = usePopoverClamp(open);

  /**
   * `MenuOption` n'a PAS de champ `disabled` : aucune option de ce menu-ci ne peut être
   * désactivée, il n'était donc pas atteint par le défaut des menus d'ACTIONS. On passe
   * malgré tout par le noyau commun — une seule navigation par flèches dans le projet,
   * et le jour où une option désactivée apparaîtra, elle sera enjambée d'office.
   */
  const enabled = useMemo(() => options.map(() => true), [options]);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // Ouvre en positionnant l'index actif sur l'option courante.
  const openMenu = useCallback(() => {
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  }, [options, value]);

  // Clic extérieur → fermeture (sans rendre le focus).
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, close]);

  // Focus de l'option active à l'ouverture / au déplacement.
  useEffect(() => {
    if (open) itemsRef.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  // Enter/Espace déclenchent le clic natif du bouton (toggle) — on ne gère ici
  // que les flèches, qui ouvrent le menu en positionnant le focus.
  const onButtonKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    if (!isRovingKey(e.key, 'vertical')) return;
    e.preventDefault();
    const next = resolveRovingIndex(e.key, activeIndex, enabled, 'vertical');
    if (next !== null) setActiveIndex(next);
  };

  const choose = (val: T): void => {
    onSelect(val);
    close();
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onButtonKeyDown}
        className={triggerClassName ?? DEFAULT_TRIGGER_CLASS}
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          id={menuId}
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className="absolute end-0 z-20 mt-1 min-w-44 rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                ref={(el) => {
                  itemsRef.current[index] = el;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                tabIndex={index === activeIndex ? 0 : -1}
                onClick={() => choose(option.value)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand aria-checked:font-semibold aria-checked:text-brand motion-reduce:transition-none"
              >
                {option.icon}
                <span className="flex-1">{option.label}</span>
                {selected && <CheckMark />}
              </button>
            );
          })}
          <span className="sr-only">{closeLabel}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Coche (Heroicon `check`, outline 24) — marque VISUELLEMENT l'option active (jamais la
 * couleur seule). Exportée pour les autres menus à état du projet (p. ex. le menu de
 * débordement des bascules d'affichage de la visionneuse), afin qu'ils ne redessinent
 * pas leur propre coche divergente.
 */
export function CheckMark(): React.JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="h-4 w-4 text-brand"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}
